import { Capacitor } from '@capacitor/core';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, indexedDBLocalPersistence, initializeAuth, type Auth } from 'firebase/auth';
import { getDatabase, type Database } from 'firebase/database';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { t } from '../i18n';

/**
 * Initialisation de Firebase.
 *
 * Ces valeurs ne sont pas des secrets : elles partent dans le bundle
 * envoye au navigateur, et Firebase est concu pour ca. Ce qui protege
 * reellement les donnees, ce sont les regles de securite de la base
 * (`database.rules.json`), pas la discretion de cette configuration.
 *
 * Elles vivent quand meme dans des variables d'environnement, pour
 * pouvoir viser un projet de test et un projet de production sans
 * toucher au code.
 */
const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/**
 * Vrai si la configuration est complete.
 * Sans elle, l'application affiche un ecran d'explication plutot que de
 * planter avec une erreur Firebase illisible.
 */
export const isFirebaseConfigured = Boolean(
  config.apiKey && config.databaseURL && config.projectId,
);

/** Ce qu'il manque, pour l'afficher a l'ecran pendant l'installation. */
export function missingFirebaseKeys(): string[] {
  return Object.entries({
    VITE_FIREBASE_API_KEY: config.apiKey,
    VITE_FIREBASE_AUTH_DOMAIN: config.authDomain,
    VITE_FIREBASE_DATABASE_URL: config.databaseURL,
    VITE_FIREBASE_PROJECT_ID: config.projectId,
    VITE_FIREBASE_APP_ID: config.appId,
  })
    .filter(([, value]) => !value)
    .map(([key]) => key);
}

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let databaseInstance: Database | null = null;
let storageInstance: FirebaseStorage | null = null;

/**
 * Cree l'application Firebase a la premiere demande.
 * L'initialisation est paresseuse pour que l'ecran d'installation
 * puisse s'afficher meme sans configuration.
 */
export function firebaseApp(): FirebaseApp {
  if (!isFirebaseConfigured) {
    throw new Error(
      t.commun.nonConfigure,
    );
  }
  if (!app) {
    app = initializeApp(config);
    poserLAttestation(app);
  }
  return app;
}

/**
 * App Check : prouver que la requete vient bien de l'application.
 *
 * # Le trou qu'il bouche
 *
 * La configuration ci-dessus part dans le bundle, et c'est voulu. Mais
 * elle suffit a parler directement a la base depuis un script : il n'y a
 * qu'a creer un compte, prendre son jeton, et lire en boucle. Les regles
 * de securite ne peuvent rien contre ca — elles verifient *qui* demande,
 * jamais *depuis ou*. Mesure sur la vraie base : la table des membres
 * pese 28 ko pour trente-deux comptes, donc environ neuf mega-octets a
 * dix mille. Relue en boucle pendant une heure, elle se compte en
 * centaines de gigaoctets de sortie, et la sortie se facture.
 *
 * App Check ajoute la question manquante, et Firebase refuse ce qui n'a
 * pas de jeton — quel que soit le compte dont dispose le demandeur.
 *
 * # Deux facons d'attester, tres inegales
 *
 * L'application tourne dans une WebView : le code qui parle a Firebase
 * est du JavaScript, pas du Swift. Laisse a lui-meme, le SDK web ne sait
 * donc attester que par reCAPTCHA — « un humain dans un navigateur »,
 * dont le jeton se recolte et se rejoue.
 *
 * Sur un telephone on peut faire bien mieux. App Attest fait signer
 * l'attestation par la Secure Enclave de la puce : c'est une preuve
 * materielle que la requete vient de *cette* application, sur un iPhone
 * authentique et non modifie. Le greffon Capacitor la recupere cote
 * natif, et `CustomProvider` se contente de la tendre au SDK web. Le
 * code metier ne change pas, la preuve devient materielle.
 *
 * Le navigateur garde reCAPTCHA, quand une cle de site est fournie.
 * L'application n'est plus destinee qu'au mobile, mais deux pages
 * doivent survivre dans un navigateur : celle ou atterrissent les liens
 * des courriels, et le relais d'ouverture. Ni l'une ni l'autre ne touche
 * a la base — elles ne parlent qu'a l'authentification, qu'App Check ne
 * filtre pas.
 *
 * # Pourquoi rien n'est encore exige
 *
 * Poser un jeton et l'exiger sont deux gestes distincts. Celui-ci pose ;
 * l'exigence se regle dans la console, service par service, et seulement
 * une fois qu'on a vu les jetons arriver. L'ordre inverse fermerait la
 * porte a tout le monde, iPhones compris.
 *
 * `isTokenAutoRefreshEnabled` fait renouveler le jeton avant qu'il
 * expire : sans lui, une session longue perd son attestation en cours de
 * route et l'application se met a echouer sans rien dire.
 */
function poserLAttestation(instance: FirebaseApp): void {
  const natif = Capacitor.isNativePlatform();
  const cle = import.meta.env.VITE_APPCHECK_SITE_KEY;
  const debogage = import.meta.env.VITE_APPCHECK_DEBUG;

  // Dans un navigateur, sans cle de site, il n'y a rien a poser.
  if (!natif && !cle) return;

  /*
    Le jeton de debogage sert aux machines qui ne peuvent pas attester :
    un poste de developpement, un simulateur — App Attest exige un vrai
    appareil. Il se declare dans la console, pour ce projet seulement.
  */
  if (!natif && debogage) {
    (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      debogage === 'true' ? true : debogage;
  }

  void (async () => {
    const { initializeAppCheck, CustomProvider, ReCaptchaEnterpriseProvider } = await import(
      'firebase/app-check'
    );

    if (natif) {
      /*
        Le greffon est installe sous un alias, `capacitor-firebase-app-check`,
        et non sous son nom d'origine. SwiftPM identifie un paquet local
        par le nom de son dossier : `@capacitor-firebase/app-check`
        s'appelait donc « app-check », comme le paquet de Google dont
        depend Firebase. Les deux entraient en collision, et la
        compilation iOS echouait sur un produit introuvable. Le dossier
        renomme, l'ambiguite disparait.
      */
      const { FirebaseAppCheck } = await import('capacitor-firebase-app-check');

      /*
        L'initialisation native n'a lieu qu'une fois, et le fournisseur
        doit l'attendre : `getToken` avant elle ne rendrait rien. On
        retient donc la promesse plutot qu'un drapeau — deux demandes
        simultanees au demarrage attendent alors la meme.
      */
      const prete = FirebaseAppCheck.initialize({ debug: Boolean(debogage) });

      initializeAppCheck(instance, {
        provider: new CustomProvider({
          getToken: async () => {
            await prete;
            const { token, expireTimeMillis } = await FirebaseAppCheck.getToken();
            return {
              token,
              // Une heure par defaut : c'est la duree de vie d'un jeton
              // App Check, et le SDK a besoin d'une echeance pour savoir
              // quand le renouveler.
              expireTimeMillis: expireTimeMillis ?? Date.now() + 3_600_000,
            };
          },
        }),
        isTokenAutoRefreshEnabled: true,
      });
      return;
    }

    initializeAppCheck(instance, {
      provider: new ReCaptchaEnterpriseProvider(cle as string),
      isTokenAutoRefreshEnabled: true,
    });
  })().catch(() => {
    /*
      Une attestation qui echoue ne doit pas emporter l'application.
      Tant que l'exigence n'est pas activee cote serveur, l'absence de
      jeton est sans consequence ; une fois activee, le refus viendra du
      serveur, avec un message clair, et non d'un plantage ici.
    */
  });
}

/**
 * Firebase Authentication.
 *
 * Dans un navigateur, `getAuth()` fait le bon choix tout seul. Dans
 * l'enveloppe native, non : il installe un resolveur de redirections qui
 * attend une fenetre surgissante et un iframe heberges par le domaine
 * d'authentification. WebKit ne les lui donne jamais, `onAuthStateChanged`
 * ne repond pas, et l'application reste sur son ecran de chargement.
 *
 * On initialise donc l'authentification a la main sur mobile, avec la
 * seule persistance dont on a besoin — nous ne nous connectons que par
 * e-mail et mot de passe, aucune redirection n'entre en jeu.
 */
export function auth(): Auth {
  if (authInstance) return authInstance;

  authInstance = Capacitor.isNativePlatform()
    ? initializeAuth(firebaseApp(), { persistence: indexedDBLocalPersistence })
    : getAuth(firebaseApp());
  return authInstance;
}

export function db(): Database {
  databaseInstance ??= getDatabase(firebaseApp());
  return databaseInstance;
}

/**
 * Cloud Storage : les photos et les messages vocaux.
 *
 * Ils vivaient dans la base, encodes en data URL. Une image en base ne se
 * met jamais en cache : le fil la retelechargeait a chaque ouverture. Sur
 * Storage elle devient une URL HTTPS que WebKit garde tout seul.
 *
 * Contrairement a l'authentification, rien de particulier a faire en
 * natif : Storage ne parle qu'en HTTPS, sans fenetre surgissante ni
 * iframe.
 */
export function storage(): FirebaseStorage {
  storageInstance ??= getStorage(firebaseApp());
  return storageInstance;
}
